<template>
  <Layout>
    <div class="row">
      <PageHeader class="col-11" title="Package Management" />
    </div>

    <!-- Search and Create new package -->
    <div class="row mb-2">
      <div class="col-sm-4">
        <div class="search-box me-2 mb-2 d-inline-block">
          <div class="position-relative">
            <b-form-input
              v-model="filter"
              type="search"
              class="form-control"
              placeholder="Search Package"
            ></b-form-input>
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </div>
      <div class="col-sm-8">
        <div class="text-sm-end">
          <button
            type="button"
            @click="showCreatePackageModal"
            class="btn btn-info btn-rounded mb-2 me-2"
          >
            <i class="mdi mdi-plus me-1"></i>
            {{ $t("menuitems.administration.list.package.list.add") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Packages Table -->
    <div class="table-responsive mb-0">
      <b-table
        class="datatables"
        :items="allPackages"
        :fields="fields"
        responsive="sm"
        :per-page="0"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter="filter"
        :filter-included-fields="filterOn"
        @filtered="onFiltered"
      >
        <template #cell(created_at)="row">
          <span
            >{{ new Date(row.item.created_at).toDateString() }}
            {{ new Date(row.item.created_at).toLocaleTimeString() }}</span
          >
        </template>

        <template #cell(is_active)="row">
          <span :class="`badge ${getStatusClass(row.item.is_active)}`">{{
            row.item.is_active ? "Active" : "Disabled"
          }}</span>
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="primary"
            class="m-1"
            @click="showPackageDetails(row.item)"
          >
            View Details
          </b-button>
        </template>
      </b-table>
    </div>
    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-end mb-3">
          <ul class="pagination pagination-rounded mb-0">
            <!-- pagination -->
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
            ></b-pagination>
          </ul>
        </div>
      </div>
    </div>

    <!-- Create new package modal -->
    <b-modal
      id="create-new-package-modal"
      title="Raise Challan"
      size="lg"
      title-class="font-18"
      no-close-on-backdrop
      hide-footer
      centered
    >
      <template #modal-header>
        <div><h5>Create New Package</h5></div>
        <div>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="$bvModal.hide('create-new-package-modal')"
          >
            Cancel
          </button>
        </div>
      </template>
      <form @submit.prevent="createNewPackage">
        <div class="row">
          <div class="col-12">
            <div class="mb-3">
              <label>Package Name</label>
              <input
                placeholder="Enter Package Name"
                v-model="createPackageObj.package_name"
                type="text"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-12">
            <div class="mb-3">
              <label>Description</label>
              <textarea
                v-model="createPackageObj.description"
                placeholder="Package Description"
                class="form-control inp-bg"
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Start Chainage</label>
              <input
                placeholder="Enter Start Chainage"
                v-model="createPackageObj.start_chainage"
                type="text"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>End Chainage</label>
              <input
                placeholder="Enter End Chainage"
                v-model="createPackageObj.end_chainage"
                type="text"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Chainage Length</label>
              <input
                placeholder="Enter End Chainage"
                v-model="createPackageObj.chainage_length"
                type="text"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Concessionaire Company</label>
              <v-select
                class="style-chooser"
                v-model="createPackageObj.concessionaire_company"
                :options="concessionaireCompanies"
                label="company_name"
                required
              ></v-select>
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Operator Company</label>
              <v-select
                class="style-chooser"
                v-model="createPackageObj.operator_company"
                :options="operatorCompanies"
                label="company_name"
                required
              ></v-select>
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>System Integrator Company</label>
              <v-select
                class="style-chooser"
                v-model="createPackageObj.integrator_company"
                :options="integratorCompanies"
                label="company_name"
                required
              ></v-select>
            </div>
          </div>
        </div>

        <hr />

        <!-- BUTTON -->
        <div class="row">
          <div class="col-12">
            <div class="mt-2 text-center">
              <button type="submit" class="btn btn-primary me-1 w-50">
                Create Package <i class="bx bx-rocket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>

    <!-- View/Edit package modal -->
    <b-modal
      id="view-package-modal"
      size="lg"
      title-class="font-18"
      hide-footer
      centered
    >
      <template #modal-header>
        <div><h5>Package Details</h5></div>
        <div>
          <button
            class="btn btn-sm btn-outline-primary"
            @click="$bvModal.hide('view-package-modal')"
          >
            Close
          </button>
        </div>
      </template>
      <form @submit.prevent="savePackageDetails">
        <div class="row">
          <div class="col-12">
            <div class="mb-3">
              <label>Package Name</label>
              <input
                placeholder="Enter Package Name"
                v-model="selectedPackageObj.package_name"
                type="text"
                class="form-control inp-bg"
                required
                :disabled="!isEditMode"
              />
            </div>
          </div>
          <div class="col-12">
            <div class="mb-3">
              <label>Description</label>
              <textarea
                v-model="selectedPackageObj.description"
                placeholder="Package Description"
                class="form-control inp-bg"
                :disabled="!isEditMode"
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Start Chainage</label>
              <input
                placeholder="Enter Start Chainage"
                v-model="selectedPackageObj.start_chainage"
                type="text"
                class="form-control inp-bg"
                required
                :disabled="!isEditMode"
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>End Chainage</label>
              <input
                placeholder="Enter End Chainage"
                v-model="selectedPackageObj.end_chainage"
                type="text"
                class="form-control inp-bg"
                required
                :disabled="!isEditMode"
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Chainage Length</label>
              <input
                placeholder="Enter End Chainage"
                v-model="selectedPackageObj.chainage_length"
                type="text"
                class="form-control inp-bg"
                required
                :disabled="!isEditMode"
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Concessionaire Company</label>
              <v-select
                class="style-chooser"
                v-model="selectedPackageObj.concessionaire_company_name"
                :options="concessionaireCompanies"
                label="company_name"
                required
                :disabled="!isEditMode"
              ></v-select>
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Operator Company</label>
              <v-select
                class="style-chooser"
                v-model="selectedPackageObj.operator_company_name"
                :options="operatorCompanies"
                label="company_name"
                required
                :disabled="!isEditMode"
              ></v-select>
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>System Integrator Company</label>
              <v-select
                class="style-chooser"
                v-model="selectedPackageObj.integrator_company_name"
                :options="integratorCompanies"
                label="company_name"
                required
                :disabled="!isEditMode"
              ></v-select>
            </div>
          </div>
        </div>

        <hr />

        <!-- BUTTON -->
        <div class="row">
          <div class="col-12">
            <div class="mt-2 text-center" v-if="!isEditMode">
              <a
                href="javascript:void()"
                class="btn btn-outline-info me-1 w-50"
                @click="isEditMode = true"
              >
                <i class="bx bx-pencil mr-2"></i> Edit Package
              </a>
            </div>
            <div class="mt-2 text-center" v-else>
              <button type="submit" class="btn btn-outline-success me-1 w-50">
                <i class="bx bx-rocket mr-2"></i> Save Package
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>
<script>
import Layout from "@/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import packageMixin from "@/mixins/administration";
import Swal from "sweetalert2";

/**
 * Dashboard Component
 */
export default {
  page: {
    title: "Package Management",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  mixins: [packageMixin],
  data() {
    return {
      createPackageObj: {},
      selectedPackageObj: {},
      isEditMode: false,
      allPackages: [],
      integratorCompanies: [],
      operatorCompanies: [],
      concessionaireCompanies: [],
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      sortBy: "id",
      sortDesc: false,
      filterOn: [],
      filter: null,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true,
        },
        {
          key: "package_name",
          label: "Package Name",
          sortable: false,
        },
        {
          key: "start_chainage",
          label: "Start Chainaige",
          sortable: false,
        },
        {
          key: "end_chainage",
          label: "End Chainage",
          sortable: false,
        },
        {
          key: "chainage_length",
          label: "Length",
          sortable: false,
        },
        {
          key: "created_at",
          label: "Created At",
          sortable: false,
        },
        {
          key: "is_active",
          sortable: false,
        },
        {
          key: "actions",
          sortable: false,
          label: "",
        },
      ],
    };
  },
  async mounted() {
    this.$store.dispatch("layout/changeLoaderValue", true);
    this.allPackages = await this.GetAllPackages();
    const companies = await this.GetAllCompanies();
    this.concessionaireCompanies = companies.filter((c) =>
      c.types.map((t) => t.type_name).includes("Concessionaire")
    );
    this.operatorCompanies = companies.filter((c) =>
      c.types.map((t) => t.type_name).includes("Operator")
    );
    this.integratorCompanies = companies.filter((c) =>
      c.types.map((t) => t.type_name).includes("System Integrator")
    );

    this.$store.dispatch("layout/changeLoaderValue", false);
  },
  methods: {
    showCreatePackageModal() {
      this.$bvModal.show("create-new-package-modal");
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    async createNewPackage() {
      try {
        this.$store.dispatch("layout/changeLoaderValue", true);
        await this.CreateNewPackage(this.createPackageObj);
        this.allPackages = await this.GetAllPackages();
        this.$store.dispatch("layout/changeLoaderValue", false);
        this.$bvModal.hide("create-new-package-modal");
        Swal.fire("Package Created", "Package created successfully", "success");
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", "Failed to create package", "error");
      }
    },

    async savePackageDetails() {
      try {
        this.$store.dispatch("layout/changeLoaderValue", true);
        delete this.selectedPackageObj.updated_at;
        delete this.selectedPackageObj.is_active;
        delete this.selectedPackageObj.created_at;
        delete this.selectedPackageObj.concessionaire_company;
        delete this.selectedPackageObj.operator_company;
        delete this.selectedPackageObj.integrator_company;

        if (
          typeof this.selectedPackageObj.concessionaire_company_name ===
          "string"
        ) {
          delete this.selectedPackageObj.concessionaire_company_name;
        }

        if (typeof this.selectedPackageObj.operator_company_name === "string") {
          delete this.selectedPackageObj.operator_company_name;
        }

        if (
          typeof this.selectedPackageObj.integrator_company_name === "string"
        ) {
          delete this.selectedPackageObj.integrator_company_name;
        }

        await this.SavePackage(this.selectedPackageObj);
        this.allPackages = await this.GetAllPackages();
        this.$store.dispatch("layout/changeLoaderValue", false);
        this.$bvModal.hide("view-package-modal");
        Swal.fire("Package Updated", "Package updated successfully", "success");
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", e.response.data.message, "error");
      }
    },

    showPackageDetails(item) {
      this.selectedPackageObj = item;
      this.isEditMode = false;
      this.$bvModal.show("view-package-modal");
    },

    getStatusClass(status) {
      switch (status) {
        case true:
          return "bg-success";

        case false:
          return "bg-danger";

        default:
          return "bg-info";
      }
    },
  },
  components: {
    Layout,
    PageHeader,
  },
};
</script>